<template>
  <base-view title="Produtos integrados  - Marketplaces" class="mb-5 mr-4">
    <b-row class="mb-5">
      <b-col cols="12" class="mb-3">
        <div class="mb-2">
          Selecione o marketplace que deseja ver os produtos integrados:
        </div>

        <b-alert show class="fs-14">
          <i class="fas fa-info-circle mr-1"></i> <b>Observação:</b> Só é
          possível ver os produtos integrados com um marketplace por vez, pois
          cada plataforma possui um processo de integração diferente.
        </b-alert>
      </b-col>
      <b-col
        cols="12"
        class="d-flex align-items-center"
        v-if="loadingEcommerces"
      >
        <b-spinner small class="mr-2" /> Carregando marketplaces
      </b-col>
      <b-col cols="12" class="d-flex align-items-center">
        <marketplace-select-card
          v-for="marketplace in marketplaces"
          :key="marketplace.id"
          :marketplace="marketplace"
          :selectedMarketplace="selectedMarketplace"
          @select="handleECommerceSelection(marketplace)"
          class="mr-4"
        />
      </b-col>
    </b-row>
    <b-row
      align-content="center"
      align-v="center"
      v-if="selectedMarketplace.id"
    >
      <b-col cols="12" class="mb-3">
        <b-input
          placeholder="Pesquise pelo nome dos produtos"
          debounce="1000"
          v-model="searchFields.productName"
          @keydown.enter="getProducts"
        >
        </b-input>
      </b-col>

      <b-col cols="12" v-if="!loading && (!products || !products.length)">
        <b-card class="mx-auto" style="max-width: 220px">
          <b-card-text
            class="d-flex flex-column align-items-center text-center"
          >
            <i class="fas fa-info-circle fs-20 text-info mb-2"></i>
            Sem produtos encontrados
          </b-card-text>
        </b-card>
      </b-col>
      <b-col v-if="Object.keys(selectedMarketplace).length">
        <b-row>
          <b-col
            cols="4"
            class="mb-3"
            v-for="(product, i) in products"
            :key="i"
          >
            <b-card>
              <div class="text-center mb-2" v-if="product.photo">
                <b-img
                  fluid
                  :src="getImageLinkByOrigin(product.origem) + product.photo"
                  style="min-height: 50px; max-height: 50px"
                >
                </b-img>
              </div>
              <div class="mb-2" :title="product.product || product.produto">
                <span
                  style="
                    overflow: hidden;
                    display: -webkit-box;
                    -webkit-box-orient: vertical;
                    -webkit-line-clamp: 2;
                    min-height: 40px;
                    max-height: 40px;
                  "
                >
                  {{ product.product || product.produto }}
                </span>
              </div>
              <div class="d-flex align-items-center mb-2">
                <i class="far fa-money-bill-alt mr-2"></i>
                <span>
                  {{ formatNumber(product.priceSalesFormat1 || product.price) }}
                </span>
              </div>
              <div :class="product.quantityStock <= 0 ? 'text-danger' : ''">
                <i class="fas fa-box mr-2"></i>
                Em estoque: {{ product.quantityStock }}
              </div>
              <div class="text-center mt-3">
                <button
                  class="btn btn-danger"
                  @click="
                    removeProduct(product.marketplaceProductIntegrationId, i)
                  "
                >
                  <i class="fas fa-trash"></i> Remover produto
                </button>
              </div>
            </b-card>
          </b-col>
        </b-row>
      </b-col>
      <b-col
        v-if="loading"
        cols="12"
        class="d-flex flex-column align-items-center mb-4"
      >
        <b-spinner label="Spinning" class="mb-2"> </b-spinner>
        Carregando seus produtos...
      </b-col>
      <b-col cols="12" v-if="!reachLastPage">
        <div v-observe-visibility="visibilityChanged"></div>
      </b-col>
    </b-row>
  </base-view>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import shopkeeperAppApi from '@/api/shopkeeperAppApi';
import makeToast from '@/mixins/makeToast';
import UserTypes from '@/enums/UserTypes';
import EnabledIntegrationStatus from 'Enums/Integration/EnabledIntegrationStatus';
import IntegrationTypes from 'Enums/Integration/IntegrationTypes';
import { formatNumber } from '@/utils';
import { getImageLinkByOrigin } from '@/utils/photos.js';
import MarketplaceSelectCard from './_components/MarketplaceSelectCard.vue';
import MarketplaceMapFolder from './_components/map-folder';

export default {
  name: 'ProductsIntegrate',
  mixins: [makeToast],
  components: {
    MarketplaceSelectCard,
  },
  data() {
    return {
      EnabledIntegrationStatus,
      IntegrationTypes,
      loading: false,
      loadingEcommerces: false,
      integrating: false,
      pagination: {
        currentPage: 0,
        itemsPerPage: 10,
        total: 0,
        lastPage: 0,
      },
      searchFields: {
        productName: '',
      },
      selectedMarketplace: {},
      products: [],
      marketplaces: [],
    };
  },
  computed: {
    ...mapState(['dadosUsuario']),
    reachLastPage() {
      return (
        this.pagination.lastPage > 0 &&
        this.pagination.currentPage >= this.pagination.lastPage
      );
    },
  },
  watch: {
    'searchFields.productName': function () {
      this.pagination = {
        currentPage: 1,
        itemsPerPage: 10,
        total: 0,
        lastPage: 0,
      };
      this.products = [];
      this.getProducts();
    },
    'pagination.currentPage': function () {
      this.getProducts();
    },
    'selectedMarketplace.id': function () {
      if (!this.selectedMarketplace.id) return;

      this.pagination = {
        currentPage: 1,
        itemsPerPage: 10,
        total: 0,
        lastPage: 0,
      };
      this.products = [];
      this.getProducts();
    },
  },
  mounted() {
    this.getEcommerces();
  },
  methods: {
    ...mapActions('shopkeeper', [
      'getSelectedProducts',
      'deleteProductMarketplace',
    ]),
    getImageLinkByOrigin,
    formatNumber,
    async getEcommerces() {
      this.loadingEcommerces = true;

      const { data } = await shopkeeperAppApi.get(
        `/integration/marketplace/${this.userTypeId()}/integrations`
      );

      this.loadingEcommerces = false;

      this.marketplaces = data.data || [];
    },
    userTypeId() {
      return this.$route.meta.userType == UserTypes.BALCONISTA
        ? this.dadosUsuario.construp_user_id
        : this.dadosUsuario.id;
    },
    getProducts() {
      if (this.loading) return;

      this.loading = true;

      this.getSelectedProducts({
        userId: this.userTypeId(),
        productName: this.searchFields.productName,
        marketplaceId: this.selectedMarketplace.id,
        integrated: true,
        currentPage: this.pagination.currentPage,
        status: 0,
      })
        .then((response) => {
          const { data, perPage, total, lastPage } = response.data.data || {};
          this.pagination.itemsPerPage = perPage || 15;
          this.pagination.total = total;
          this.pagination.lastPage = lastPage;
          this.products.push(...(data || []));
        })
        .finally(() => {
          this.loading = false;
        });
    },
    handleECommerceSelection(marketplace) {
      if (!marketplace.enabledIntegration) return;

      if (
        marketplace.enabledIntegration.status !==
        EnabledIntegrationStatus.ACTIVE
      )
        return;

      if (this.selectedMarketplace.id === marketplace.id) {
        this.selectedMarketplace = {};
        return;
      }

      this.selectedMarketplace = marketplace;
    },
    visibilityChanged(isVisible) {
      if (!isVisible || this.loading) return;

      if (this.reachLastPage) return;

      this.pagination.currentPage = this.pagination.currentPage + 1;
    },
    removeProduct(id, i) {
      const data = {
        construpUserId: this.userTypeId(),
        id,
        marketplaceId: this.selectedMarketplace.id,
      };

      this.deleteProductMarketplace(data)
        .then((response) => {
          if (response.data.success) {
            this.makeToast(
              'Sucesso',
              'Produto removido com sucesso!',
              'success'
            );

            this.products.splice(i, 1);
          } else {
            this.makeToast(
              'Erro',
              response.data.message || 'Erro ao remover produto.',
              'danger'
            );
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>
